import { EmailTemplateType } from '@dealstack/shared/lib/generated';
import type { EmailTemplateSummaryRes } from '@dealstack/shared/lib/generated';

export const TEMPLATE_CATEGORIES = [
	'invitation',
	'document_completion',
	'kyc',
	'funds_transfer',
	'other',
] as const;

export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number];

export interface TemplateCategoryGroup {
	category: TemplateCategory;
	templates: EmailTemplateSummaryRes[];
}

const CATEGORISED_TEMPLATES: ReadonlyArray<[EmailTemplateType, TemplateCategory]> = [
	[EmailTemplateType.INVITE_ADMIN_SSO, 'invitation'],
	[EmailTemplateType.INVITE_EXISTING_USER_TO_PROGRAMME, 'invitation'],
	[EmailTemplateType.INVITE_NEW_USER_TO_PROGRAMME, 'invitation'],
	[EmailTemplateType.INVITE_USER_EXISTING_INVESTOR, 'invitation'],
	[EmailTemplateType.INVITE_USER_INVESTOR, 'invitation'],
	[EmailTemplateType.INVITE_INVESTOR_SSO, 'invitation'],
	[EmailTemplateType.IP_ADDITIONAL_ALLOCATION, 'invitation'],
	[EmailTemplateType.IP_LEAVER, 'invitation'],
	[EmailTemplateType.INVITE_EXISTING_USER_TO_DEAL, 'invitation'],
	[EmailTemplateType.INVITE_NEW_USER_TO_DEAL, 'invitation'],

	[EmailTemplateType.INVITE_USER_SEND_DOCUMENT, 'document_completion'],
	[EmailTemplateType.DOCUMENT_SIGNATURE_REQUEST, 'document_completion'],
	[EmailTemplateType.AUTHORISED_SIGNATORY_SIGNING_INVITATION, 'document_completion'],
	[EmailTemplateType.REPRESENTATIVE_SIGNING_INVITATION, 'document_completion'],
	[EmailTemplateType.WITNESS_SIGNING_INVITATION, 'document_completion'],
	[EmailTemplateType.SEND_SDW_DOCUMENTS, 'document_completion'],

	[EmailTemplateType.SEND_KYC_REQUEST, 'kyc'],
	[EmailTemplateType.KYC_INFO_REQUEST, 'kyc'],
	[EmailTemplateType.KYC_REJECTION, 'kyc'],
	[EmailTemplateType.KYC_CERTIFIER_INVITATION, 'kyc'],

	[EmailTemplateType.FUNDS_TRANSFER_CONFIRMATION, 'funds_transfer'],
	[EmailTemplateType.FUNDS_TRANSFER_REJECTION, 'funds_transfer'],

	[EmailTemplateType.BANK_ACCOUNT_REJECTION, 'other'],
	[EmailTemplateType.SEND_DOWNLOAD_LINK, 'other'],
];

const CATEGORY_BY_TYPE = new Map<EmailTemplateType, TemplateCategory>(CATEGORISED_TEMPLATES);

const ORDER_BY_TYPE = new Map<EmailTemplateType, number>(
	CATEGORISED_TEMPLATES.map(([type], index) => [type, index])
);

export const getTemplateCategory = (type: EmailTemplateType): TemplateCategory =>
	CATEGORY_BY_TYPE.get(type) ?? 'other';

const compareByDesignOrder = (a: EmailTemplateSummaryRes, b: EmailTemplateSummaryRes): number =>
	(ORDER_BY_TYPE.get(a.templateType) ?? Number.MAX_SAFE_INTEGER) -
	(ORDER_BY_TYPE.get(b.templateType) ?? Number.MAX_SAFE_INTEGER);

export const groupTemplatesByCategory = (
	templates: EmailTemplateSummaryRes[]
): TemplateCategoryGroup[] =>
	TEMPLATE_CATEGORIES.map((category) => ({
		category,
		templates: templates
			.filter((template) => getTemplateCategory(template.templateType) === category)
			.sort(compareByDesignOrder),
	})).filter((group) => group.templates.length > 0);
